// src/features/files/components/CreateFolderDialog.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useFolders } from '../hooks/useFolders';

const EMPTY_FORM = { folder_name: '', description: '', shared: false };

/**
 * New folder in `parentFolderId` (null: top level). With `canShare`, it can be
 * shared with all departments (the department that manages the global files,
 * at the top level; folders in a shared folder are shared with it anyway).
 */
export default function CreateFolderDialog({ open, onClose, parentFolderId, onSuccess, canShare = false }) {
  const { t } = useTranslation(['files', 'common']);
  const { createFolder, loading } = useFolders();
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.folder_name.trim()) {
      setError(t('files:errors.folder_name_required'));
      return;
    }

    const result = await createFolder({
      folder_name: formData.folder_name.trim(),
      description: formData.description.trim() || null,
      parent_folder_id: parentFolderId,
      is_system_folder: canShare && formData.shared,
      is_protected: false,
    });

    if (result?.success) {
      setFormData(EMPTY_FORM);
      onSuccess?.();
      onClose();
    } else {
      setError(result?.error || t('common:errors.generic'));
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(EMPTY_FORM);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('files:create_folder')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            <TextField
              label={t('files:folder_name')}
              value={formData.folder_name}
              onChange={handleChange('folder_name')}
              required
              fullWidth
              autoFocus
              disabled={loading}
            />
            
            <TextField
              label={t('common:labels.description')}
              value={formData.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={3}
              disabled={loading}
            />

            {canShare && (
              <FormControlLabel
                control={(
                  <Switch
                    checked={formData.shared}
                    onChange={(event) => setFormData({ ...formData, shared: event.target.checked })}
                    disabled={loading}
                  />
                )}
                label={t('files:shared.share')}
                slotProps={{ typography: { variant: 'body2' } }}
              />
            )}
            {canShare && formData.shared && (
              <Alert severity="info" sx={{ mt: -1 }}>{t('files:shared.share_hint')}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common:buttons.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.folder_name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : t('common:buttons.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}